// exports curvesFromSvgPath

import { map, invokeMap, isString, isArray } from 'lodash';

import { deg2rad, rad2deg } from 'gear';

import { Point } from './point';
import { Matrix } from './matrix';
import { Cubic } from './bezier';
import { Segment, Curve } from './curve';


const valuesStringToArray = function (data) {
  const rp = data.split(/[\s,]+/);
  const values = [];
  for( let k = 0; k < rp.length; k++) {
    const rpk = rp[k];
    let c = '',
        dot = false;
    for( let n = 0; n < rpk.length; n++) {
      if(n > 0 && rpk[n] === '-' && rpk[n - 1] !== 'e') {
        values.push(parseFloat(c));
        c = '';
        dot = false;
      }
      if(rpk[n] === 'e') {
        dot = false;
      }
      if(rpk[n] === '.') {
        if(dot) {
          values.push(parseFloat(c));
          c = '';
          dot = false;
        }
        else {
          dot = true;
        }
      }
      c += rpk[n];
    }
    if(c !== '') {
      values.push(parseFloat(c));
    }
  }
  return values;
};

export const svgPathStringToArray = function (s) {
  let mode = null, path = [], data = '', r;
  for(let i = 0; i < s.length; ++i) {
    switch (s[i]) {
      case 'Z': case 'z': case 'M': case 'm': case 'L': case 'l': case 'c': case 'C': case 'q': case 'Q':
      case 'V': case 'v': case 'H': case 'h': case 's': case 'S':
        if(mode) {
          r = valuesStringToArray(data);
          path.push([mode].concat(r));
        }
        mode = s[i];
        data = '';
        break;

      default:
        data += s[i];
    }
  }
  if(mode) {
    r = valuesStringToArray(data);
    path.push([mode].concat(r));
  }
  return path;
};

const svgCommandToArray = function(c) {
  const sub = [];
  const cmd = c.type || c.pathSegTypeAsLetter;
  switch( cmd ) {
    case "C":
    case "c":
      sub.unshift(c.y2); sub.unshift(c.x2);
    case "Q":
    case "q":
      sub.unshift(c.y1); sub.unshift(c.x1);
    case "M":
    case "m":
    case "L":
    case "l":
      sub.push(c.x); sub.push(c.y);
      break;
    case "A":
    case "a":
      sub.push(c.r1); sub.push(c.r2);
      sub.push(c.angle);
      sub.push(c.largeArcFlag);
      sub.push(c.sweepFlag);
      sub.push(c.x); sub.push(c.y);
      break;
    case "H":
    case "h":
      sub.push(c.x);
      break;
    case "V":
    case "v":
      sub.push(c.y);
      break;
    case "S":
    case "s":
      sub.push(c.x2); sub.push(c.y2);
      sub.push(c.x); sub.push(c.y);
      break;
    case "T":
    case "t":
      sub.push(c.x); sub.push(c.y);
      break;
  }
  sub.unshift(cmd);
  return sub;
};

// PathDataQuadraticBezierCommands
// http://www.w3.org/TR/SVGTiny12/paths.html#PathDataQuadraticBezierCommands

// [ [ 'M', 20, 30 ], [ 'L', 50, 40 ], ['Z'] ]

export const curvesFromSvgPath = function(s,config ) {

  if( isString(s) ) {
    s = svgPathStringToArray(s);
  }

  // We should invert this, convert the array based rep to { type: 'M', x: ... , y: ... }
  if( ! isArray(s[0]) ) {
    s = map(s, svgCommandToArray );
  }

  // Used by font decoding, looks like there is a bug in opentype.js that spits open curves
  // We play safe and force this open curves to be closed in FontData
  const closedCurve = true;
  //const openCurve   = ( config && config.forceClosed ) ? true : false;

  let pieces = null;
  const curves = [];

  // All the paths starts with a M command

  let lp = new Point(0, 0), cp = lp, lc = lp;

  for( let d = 0 ; d < s.length ; ++d) {
    const command = s[d];
    const mode = command[0];

    let relative, m;

    const noParse = (mode === 'v' || mode === 'V' || mode === 'H' || mode === 'h' || mode === 'A' || mode === 'a' || mode === 'z' || mode === 'Z' );
    const points = [];
    if( !noParse ) {
      for(let i = 1; i < command.length; i += 2) {
        points.push(new Point(command[i], command[i + 1]));
      }
    }
    let p0, p1, p2;

    switch (mode) {

      // closepath
      case 'Z': case 'z':
        if( pieces && pieces.length > 0) {
          curves.push(Curve.fromNonTiedPieces(pieces, closedCurve));
        }
        pieces = null;
        break;

      // moveto
      case 'M': case 'm': relative = mode === 'm';
        if(pieces && pieces.length > 0) {
          curves.push(Curve.fromNonTiedPieces(pieces, closedCurve));
        }
        pieces = [];

        p0 = points[0];
        if(relative) { p0.add(cp); }
        lp = p0;

        for(m = 1; m < points.length; ++m) {
          // lineTo commands
          p0 = points[m];
          if(relative) { p0.add(cp); }
          pieces.push(new Segment(lp, p0));
          lp = p0;
        }
        cp = lc = lp;
        break;

      // lineto
      case 'L': case 'l': relative = mode === 'l';

        for(m = 0; m < points.length; ++m) {
          // lineTo commands
          p0 = points[m];
          if(relative) { p0.add(cp); }
          pieces.push(new Segment(lp, p0));
          lp = p0;
        }
        cp = lc = lp;
        break;

      // horizontal lineto
      case 'H': case 'h': relative = mode === 'h';
        for(m = 1; m < command.length; ++m) {
          const x = command[m];
          p0 = cp.clone();
          if(relative) {
            p0.x += x;
          }
          else {
            p0.x = x;
          }
          pieces.push(new Segment(lp, p0));
          lp = p0;
        }
        cp = lc = lp;
        break;

      // vertical lineto
      case 'V': case 'v': relative = mode === 'v';
        for(m = 1; m < command.length; ++m) {
          const y = command[m];
          p0 = cp.clone();
          if(relative) {
            p0.y += y;
          }
          else {
            p0.y = y;
          }
          pieces.push(new Segment(lp, p0));
          lp = p0;
        }
        cp = lc = lp;
        break;

      // curveto
      case 'C': case 'c': relative = mode === 'c';
        for(m = 0; m < points.length; m += 3) {
          p0 = points[m + 0]; if(relative) { p0.add(cp); }
          p1 = points[m + 1]; if(relative) { p1.add(cp); }
          p2 = points[m + 2]; if(relative) { p2.add(cp); }
          pieces.push(new Cubic(lp, p0, p1, p2));
          lc = p1;
          lp = p2;
        }
        cp = lp;
        break;

      // shorthand/smooth curveto
      case 'S': case 's': relative = mode === 's';
        for(m = 0; m < points.length; m += 2) {
          p0 = new Point((2 * lp.x - lc.x), (2 * lp.y - lc.y));
          p1 = points[m + 0]; if(relative) { p1.add(cp); }
          p2 = points[m + 1]; if(relative) { p2.add(cp); }
          pieces.push(new Cubic(lp, p0, p1, p2));
          lc = p1;
          lp = p2;
        }
        cp = lp;
        break;

      // quadratic Bézier curveto Q q
      case 'Q': case 'q': relative = mode === 'q';
        for(m = 0; m < points.length; m += 2) {
          p0 = points[m + 0]; if(relative) { p0.add(cp); }
          p1 = points[m + 1]; if(relative) { p1.add(cp); }
          pieces.push(Cubic.fromQuadraticPoints(lp, p0, p1));
          lc = p0;
          lp = p1;
        }
        cp = lp;
        break;

      // Shorthand/smooth quadratic Bézier curveto T t
      case 'T': case 't': relative = mode === 't';
        for(m = 0; m < points.length; m += 1) {
          p0 = new Point((2 * lp.x - lc.x), (2 * lp.y - lc.y));
          p1 = points[m + 0]; if(relative) { p1.add(cp); }
          pieces.push(Cubic.fromQuadraticPoints(lp, p0, p1));
          lc = p0;
          lp = p1;
        }
        cp = lp;
        break;

       /*
       * SVG arc representation uses "endpoint parameterisation" where we specify the endpoint of the arc.
       * This is to be consistent with the other path commands.  However we need to convert this to "centre point
       * parameterisation" in order to calculate the arc. Handily, the SVG spec provides all the required maths
       * in section "F.6 Elliptical arc implementation notes".
       *
       * Some of this code has been borrowed from the Batik library (Apache-2 license).
       * https://code.google.com/p/androidsvg/source/browse/src/com/caverock/androidsvg/SVGAndroidRenderer.java#2451
       */
      case 'A': case 'a': relative = mode === 'a';
        for(m = 0; m < command.length; m += 8) {
          let lastX = lp.x,
              lastY = lp.y;

          if( relative ) {
            lastX -= cp.x;
            lastY -= cp.y;
          }

          let rx = command[m + 1];
          let ry = command[m + 2];

          const angle        = command[m + 3];
          const largeArcFlag = command[m + 4];
          const sweepFlag    = command[m + 5];
          const _x            = command[m + 6];
          const _y            = command[m + 7];

          if(lastX === _x && lastY === _y) {
            // If the endpoints (x, y) and (x0, y0) are identical, then this
            // is equivalent to omitting the elliptical arc segment entirely.
            // (behaviour specified by the spec)
            break;
          }

          // Handle degenerate case (behaviour specified by the spec)
          if(rx === 0 || ry === 0) {
            p0 = new Point(lastX,lastY);
            if(relative) { p0.add(cp); }
            pieces.push(new Segment(lp, p0));
            return;
          }


          // Sign of the radii is ignored (behaviour specified by the spec)
          rx = Math.abs(rx);
          ry = Math.abs(ry);

          // Convert angle from degrees to radians
          const angleRad = deg2rad(angle % 360.0);
          const cosAngle = Math.cos(angleRad);
          const sinAngle = Math.sin(angleRad);

          // We simplify the calculations by transforming the arc so that the origin is at the
          // midpoint calculated above followed by a rotation to line up the coordinate axes
          // with the axes of the ellipse.

          // Compute the midpoint of the line between the current and the end point
          const dx2 = (lastX - _x) / 2.0;
          const dy2 = (lastY - _y) / 2.0;

          // Step 1 : Compute (x1', y1') - the transformed start point
          const x1 = (cosAngle * dx2 + sinAngle * dy2);
          const y1 = (-sinAngle * dx2 + cosAngle * dy2);

          let rx_sq = rx * rx;
          let ry_sq = ry * ry;

          const x1_sq = x1 * x1;
          const y1_sq = y1 * y1;

          // Check that radii are large enough.
          // If they are not, the spec says to scale them up so they are.
          // This is to compensate for potential rounding errors/differences between SVG implementations.
          const radiiCheck = x1_sq / rx_sq + y1_sq / ry_sq;
          if(radiiCheck > 1) {
            rx = (Math.sqrt(radiiCheck) * rx);
            ry = (Math.sqrt(radiiCheck) * ry);
            rx_sq = rx * rx;
            ry_sq = ry * ry;
          }

          // Step 2 : Compute (cx1, cy1) - the transformed centre point
          let sign = (largeArcFlag === sweepFlag) ? -1 : 1;

          let sq = ((rx_sq * ry_sq) - (rx_sq * y1_sq) - (ry_sq * x1_sq)) / ((rx_sq * y1_sq) + (ry_sq * x1_sq));
          sq = (sq < 0) ? 0 : sq;

          const coef = (sign * Math.sqrt(sq));
          const cx1 = coef * ((rx * y1) / ry);
          const cy1 = coef * -((ry * x1) / rx);

          // Step 3 : Compute (cx, cy) from (cx1, cy1)
          const sx2 = (lastX + _x) / 2.0;
          const sy2 = (lastY + _y) / 2.0;
          const cx = sx2 + (cosAngle * cx1 - sinAngle * cy1);
          const cy = sy2 + (sinAngle * cx1 + cosAngle * cy1);

          // Step 4 : Compute the angleStart (angle1) and the angleExtent (dangle)
          const ux = (x1 - cx1) / rx;
          const uy = (y1 - cy1) / ry;
          const vx = (-x1 - cx1) / rx;
          const vy = (-y1 - cy1) / ry;

          let p, n;

          // Compute the angle start
          n = Math.sqrt((ux * ux) + (uy * uy));
          p = ux; // (1 * ux) + (0 * uy)
          sign = (uy < 0) ? -1.0 : 1.0;
          let angleStart = rad2deg(sign * Math.acos(p / n));

          // Compute the angle extent
          n = Math.sqrt((ux * ux + uy * uy) * (vx * vx + vy * vy));
          p = ux * vx + uy * vy;
          sign = (ux * vy - uy * vx < 0) ? -1.0 : 1.0;
          let angleExtent = rad2deg(sign * Math.acos(p / n));
          if(!sweepFlag && angleExtent > 0) {
            angleExtent -= 360;
          }
          else if(sweepFlag && angleExtent < 0) {
            angleExtent += 360;
          }
          angleExtent %= 360;
          angleStart %= 360;

          // arcPoints is an array of N pieces given by N*(3*points)
          const arcPoints = arcToBeziers(angleStart, angleExtent);

          // Calculate a transformation matrix that will move and scale these bezier points to the correct location.
          //matrix
          let mr = Matrix.scale(rx, ry);
          //rotate (angle);
          mr = Matrix.multiply(Matrix.rotate(angle), mr);
          //translate (cx, cy);
          mr = Matrix.multiply(Matrix.translate(cx, cy), mr);

          invokeMap(arcPoints,'transform',mr);

          for(let k=0,kEnd=arcPoints.length; k<kEnd; k+=3 ) {
            p0 = arcPoints[k + 0]; if(relative) { p0.add(cp); }
            p1 = arcPoints[k + 1]; if(relative) { p1.add(cp); }
            p2 = arcPoints[k + 2]; if(relative) { p2.add(cp); }
            pieces.push(new Cubic(lp,p0,p1,p2));
            lp = p2;
          }
        }
        cp = lp;
        break;

      default:
        break;
    }
  }
  if(pieces && pieces.length > 0) {
    curves.push(Curve.fromNonTiedPieces(pieces, closedCurve));
  }
  return curves;
};

/*
* Generate the control points and endpoints for a set of bezier curves that match
* a circular arc starting from angle 'angleStart' and sweep the angle 'angleExtent'.
* The circle the arc follows will be centred on (0,0) and have a radius of 1.0.
*
* Each bezier can cover no more than 90 degrees, so the arc will be divided evenly
* into a maximum of four curves.
*
* The resulting control points will later be scaled and rotated to match the final
* arc required.
*
* The returned array has the format [x0,y0, x1,y1,...] and excludes the start point
* of the arc.
*/
const arcToBeziers = function(angleStart, angleExtent) {
  const numSegments = Math.ceil(Math.abs(angleExtent) / 90.0);

  angleStart = deg2rad(angleStart);
  angleExtent = deg2rad(angleExtent);
  const angleIncrement = angleExtent / numSegments;

  // The length of each control point vector is given by the following formula.
  const controlLength = 4.0 / 3.0 * Math.sin(angleIncrement / 2.0) / (1.0 + Math.cos(angleIncrement / 2.0));

  // numSegments * 3
  const points = [];

  for( let i=0; i<numSegments; i++) {
    let angle = angleStart + i * angleIncrement;
    // Calculate the control vector at this angle
    let dx = Math.cos(angle),
        dy = Math.sin(angle);
    // First control point
    points.push( new Point( dx - controlLength * dy ,
                            dy + controlLength * dx ) );
    // Second control point
    angle += angleIncrement;
    dx = Math.cos(angle);
    dy = Math.sin(angle);
    points.push( new Point( dx + controlLength * dy ,
                            dy - controlLength * dx ) );
    // Endpoint of bezier
    points.push( new Point( dx, dy ) );
  }
  return points;
};



/** WEBPACK FOOTER **
 ** ./src/cdl/geometry/svg-path.js
 **/